import React, { useState } from "react";
import {
  questionsAPI,
  QUESTION_CATEGORIES,
} from "@/entities/support/api/questions-api";
import { showAlert } from "@/shared/lib/alert";

interface NewInquiryFormProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * NewInquiryForm - 새로운 문의 작성 폼 컴포넌트
 */
export const NewInquiryForm = ({ onClose, onSuccess }: NewInquiryFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_number: "",
    category: "",
    title: "",
    content: "",
    is_public: true,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "radio" ? value === "public" : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.phone_number ||
      // !formData.category ||
      !formData.title ||
      !formData.content
    ) {
      showAlert("모든 필드를 입력해주세요.");
      return;
    }

    if (!isAgreed) {
      showAlert("개인 정보 수집에 동의해주세요.");
      return;
    }

    setIsLoading(true);

    try {
      await questionsAPI.addQuestion({
        title: formData.title,
        content: formData.content,
        category: formData.category,
        section: "inquiries",
        name: formData.name,
        email: formData.email,
        phone_number: formData.phone_number,
        is_public: formData.is_public,
      });

      showAlert("문의가 등록되었습니다.");
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Failed to submit inquiry:", error);
      showAlert(
        error instanceof Error ? error.message : "문의 등록에 실패했습니다."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-black">문의 하기</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">이름</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="이름을 입력해 주세요."
              className="px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Email */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">
              공인된 이메일
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="공인된 이메일을 입력해 주세요."
              className="px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Phone Number */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">
              휴대전화 번호
            </label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="휴대전화 번호를 입력해 주세요."
              className="px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Category */}
          {/* <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">
              문의 유형
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
            >
              <option value="">문의 유형을 선택해 주세요.</option>
              {QUESTION_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div> */}

          {/* Title */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">제목</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="제목을 입력해 주세요."
              className="px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Content */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">내용</label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="내용을 입력해 주세요."
              rows={8}
              className="px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
            />
          </div>

          {/* Is Public */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">
              공개 여부
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="is_public"
                  value="private"
                  checked={!formData.is_public}
                  onChange={handleChange}
                  className="cursor-pointer"
                />
                <span className="text-sm text-gray-600">비공개</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="is_public"
                  value="public"
                  checked={formData.is_public}
                  onChange={handleChange}
                  className="cursor-pointer"
                />
                <span className="text-sm text-gray-600">공개</span>
              </label>
            </div>
          </div>

          {/* Privacy Agreement */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="agree"
              checked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
              className="mt-1"
            />
            <label
              htmlFor="agree"
              className="text-xs text-gray-600 cursor-pointer"
            >
              개인 정보 수집 및 이용에 동의합니다
              <span className="text-orange-500 font-medium ml-1">(필수)</span>
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-6">
            <button
              type="submit"
              disabled={isLoading}
              className="px-8 py-3 bg-orange-500 text-white rounded hover:bg-orange-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "등록중..." : "등록하기"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
